import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';


import {CopyButton} from 'src/components/copy-button';
import {Skeleton} from 'src/components/skeleton';
import {
    useAccountStatuses,
    useCountries,
} from 'src/hooks/dictionaries';
import {
    selectAccountInformation,
    selectCrmId,
    selectPbIds,
} from 'src/modules/profile/account/tabs/account-profile/services/account-profile-slice';
import {InformationPill} from 'src/modules/profile/components/indormation-pill';
import {getAccountPage} from 'src/openapi/admin/apis/AccountInfoApi';
import {getAccountCrmId} from 'src/openapi/admin/apis/GetAccountCrmIdApi';
import {getAccountPbIds} from 'src/openapi/admin/apis/GetAccountPbIdsApi';
import {useAppDispatch} from 'src/store/hooks';

import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';

import {InformationSectionSkeleton} from './information-section-skeleton';

type Props = {
    accountId: number,
};

export const InformationSection: React.FC<Props> = ({accountId}) => {
    const apiDispatch = useAppDispatch();
    const information = useSelector(selectAccountInformation);
    const crmId = useSelector(selectCrmId);
    const pbIds = useSelector(selectPbIds);

    const {countries} = useCountries();
    const {accountStatuses} = useAccountStatuses();

    useEffect(() => {
        if (!information) {
            apiDispatch(getAccountPage({accountId}));
        }
    }, [information, accountId, apiDispatch]);

    useEffect(() => {
        if (!crmId) {
            apiDispatch(getAccountCrmId({accountId}));
        }
    }, [crmId, accountId, apiDispatch]);

    useEffect(() => {
        if (!crmId) {
            apiDispatch(getAccountPbIds({accountId}));
        }
    }, [crmId, accountId, apiDispatch]);


    return (
        <Skeleton view={<InformationSectionSkeleton/>} shown={!information}>
            <FormSection headline="Account Information">
                {information ? (
                    <div className="pb-s">
                        <Group gap="none" wrap="wrap" alignItems="start">
                            {countries && information.country ? (
                                <InformationPill
                                    label="Country"
                                    value={countries.find(({code}) => code === information.country)?.description}
                                />
                            ) : null}
                            {accountStatuses && information.status ? (
                                <InformationPill
                                    label="Account Status"
                                    value={
                                        accountStatuses.find(
                                            ({code}) => code === information.status,
                                        )?.description
                                    }
                                />
                            ) : null}
                            <InformationPill
                                label="Salesforce Communities"
                                value={information.helpCenterEnabled ? 'Enabled' : 'Disabled'}
                            />
                            <InformationPill
                                label="Live Agent"
                                value={information.liveChatEnabled ? 'Enabled' : 'Disabled'}
                            />
                            <InformationPill
                                label="PB Salesforce ID"
                                value={crmId?.value ? (
                                    <Group key={crmId?.value} gap="xs" alignItems="center">
                                        {crmId?.value}
                                        {crmId?.value ? <CopyButton copiedText={crmId?.value} entityName="PB Salesforce ID"/> : null}
                                    </Group>
                                ) : '--'}
                            />
                            <InformationPill
                                label="PB RTS ID"
                                value={pbIds?.length ? (
                                    <Group direction="v" alignItems="start">
                                        {pbIds.map(pbId => (
                                            <Group key={pbId} gap="xs" alignItems="center">
                                                {pbId}
                                                <CopyButton copiedText={pbId} entityName="PB RTS ID"/>
                                            </Group>
                                        ))}
                                    </Group>
                                ) : '--'}
                            />
                        </Group>
                    </div>
                ) : null}
            </FormSection>
        </Skeleton>
    );
};
